import React, { useEffect } from "react";
import {
  FaEnvelope,
  FaFlag,
  FaGithub,
  FaGlobe,
  FaLinkedin,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaPrint,
  FaUser,
} from "react-icons/fa";
import classes from "./Cv.module.css";

// Replica HTML del curriculum in formato europeo, con qualche elemento grafico

const EuFlag = () => (
  <svg className={classes.flag} viewBox="0 0 72 48" aria-label="Unione Europea">
    <rect width="72" height="48" rx="3" fill="#003399" />
    {Array.from({ length: 12 }, (_, i) => {
      const a = (i * Math.PI) / 6;
      return (
        <text key={i} x={36 + Math.sin(a) * 14} y={24 - Math.cos(a) * 14 + 2.4} fontSize="7" textAnchor="middle" fill="#ffcc00">
          ★
        </text>
      );
    })}
  </svg>
);

const Row = ({ label, icon, children }) => (
  <div className={classes.row}>
    <div className={classes.label}>{label}</div>
    <div className={classes.value}>
      {icon && <span className={classes.icon}>{icon}</span>}
      {children}
    </div>
  </div>
);

const Section = ({ children }) => (
  <div className={`${classes.row} ${classes.section}`}>
    <div className={classes.label}>
      <h2>{children}</h2>
    </div>
    <div className={classes.sectionLine} />
  </div>
);

const Link = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer">
    {children}
  </a>
);

const Job = ({ date, employer, sector, role, children }) => (
  <div className={`${classes.block} ${classes.timeline}`}>
    <Row label="• Date (da – a)">
      <span className={classes.date}>{date}</span>
    </Row>
    <Row label="• Nome e indirizzo del datore di lavoro">{employer}</Row>
    <Row label="• Tipo di azienda o settore">{sector}</Row>
    <Row label="• Tipo di impiego">{role}</Row>
    <Row label="• Principali mansioni e responsabilità">{children}</Row>
  </div>
);

const Edu = ({ date, institute, subjects, qualification, level }) => (
  <div className={`${classes.block} ${classes.timeline}`}>
    <Row label="• Date (da – a)">
      <span className={classes.date}>{date}</span>
    </Row>
    <Row label="• Nome e tipo di istituto di istruzione o formazione">{institute}</Row>
    <Row label="• Principali materie / abilità professionali oggetto dello studio">{subjects}</Row>
    <Row label="• Qualifica conseguita">
      <strong>{qualification}</strong>
    </Row>
    <Row label="• Livello nella classificazione nazionale (se pertinente)">{level}</Row>
  </div>
);

// Livello lingua: "buono" = 3 pallini su 5
const Level = ({ text, value }) => (
  <span className={classes.level}>
    {[1, 2, 3, 4, 5].map((n) => (
      <i key={n} className={n <= value ? classes.levelOn : undefined} />
    ))}
    {text}
  </span>
);

const TECH = [
  ["Linguaggi di Programmazione", ["PHP", "JavaScript", "Java", "Node.js", "Python", "TypeScript"]],
  ["Framework", ["Laravel", "React", "Angular", "Spring Boot", "Express.js", "NextJS"]],
  ["Database", ["MySQL", "MongoDB", "PostgreSQL"]],
  ["Sistemi Operativi", ["Linux", "Windows"]],
  ["Networking", ["Configurazione e gestione reti Cisco"]],
  ["DevOps & Cloud", ["Docker", "AWS", "Google Cloud", "Gestione server Linux", "Apache", "Plesk", "CPanel"]],
  ["Machine Learning & AI", ["Python", "TensorFlow", "PyTorch (in fase di studio)"]],
  ["API", ["RESTful API", "GraphQL"]],
  ["Web 3D", ["Three.js", "React Three Fiber"]],
  ["Strumenti di Grafica", ["Photoshop", "Blender", "Unity"]],
];

const Cv = () => {
  useEffect(() => {
    // Pagina raggiungibile solo con link diretto: niente indicizzazione
    const prevTitle = document.title;
    document.title = "Curriculum vitae — Davide Michele Balice";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.title = prevTitle;
      meta.remove();
    };
  }, []);

  return (
    <div className={classes.cv}>
      <button type="button" className={classes.printBtn} onClick={() => window.print()}>
        <FaPrint /> Stampa / PDF
      </button>

      <article className={classes.page}>
        <div className={classes.topBar} />

        {/* Intestazione */}
        <div className={classes.head}>
          <div className={classes.headLeft}>
            <span className={classes.title}>curriculum vitae</span>
            <EuFlag />
          </div>
          <div className={classes.headRight}>
            <div className={classes.headName}>
              <h1>
                Davide Michele <span>Balice</span>
              </h1>
              <p>Full Stack Developer | Software Engineer | AI Integration</p>
            </div>
            <div className={classes.photoFrame}>
              <img src="/assets/images/db.jpg" alt="Davide Michele Balice" className={classes.photo} />
            </div>
          </div>
        </div>

        <Section>Informazioni personali</Section>
        <div className={classes.block}>
          <Row label="Nome" icon={<FaUser />}>
            <strong>Davide Michele Balice</strong>
          </Row>
          <Row label="Indirizzo" icon={<FaMapMarkerAlt />}>
            Reggio Calabria
          </Row>
          <Row label="Telefono" icon={<FaPhoneAlt />}>
            <a href="tel:+393464709672">
              <strong>346.4709672</strong>
            </a>
          </Row>
          <Row label="E-mail" icon={<FaEnvelope />}>
            <a href="mailto:davide.balice@gmail.com">
              <strong>davide.balice@gmail.com</strong>
            </a>
          </Row>
          <Row label="Sito web" icon={<FaGlobe />}>
            <Link href="https://www.davidebalice.dev">www.davidebalice.dev</Link> - <Link href="https://www.webhub.it">www.webhub.it</Link>
          </Row>
          <Row label="Github" icon={<FaGithub />}>
            <Link href="https://github.com/davidebalice">github.com/davidebalice</Link>
          </Row>
          <Row label="Linkedin" icon={<FaLinkedin />}>
            <Link href="https://www.linkedin.com/in/davide-balice-46b36b30">www.linkedin.com/in/davide-balice-46b36b30</Link>
          </Row>
          <Row label="Nazionalità" icon={<FaFlag />}>
            italiana
          </Row>
        </div>

        <Section>Presentazione</Section>
        <div className={classes.block}>
          <Row label="">
            <p className={classes.subtitle}>Full Stack Developer | Software Engineer | AI Integration</p>
            <div className={classes.prose}>
              <p>
                Sviluppatore Full Stack con oltre 20 anni di esperienza nella progettazione e realizzazione di applicazioni web,
                piattaforme gestionali e soluzioni software personalizzate.
              </p>
              <p>
                Nel corso della mia carriera ho maturato competenze trasversali nello sviluppo frontend e backend, lavorando con
                tecnologie quali{" "}
                <strong>PHP, JavaScript, TypeScript, React, Node.js, Laravel, Angular, Java, Spring Boot, MySQL e MongoDB</strong>,
                oltre che nella progettazione di API REST e nell'integrazione di sistemi e servizi esterni.
              </p>
              <p>
                Mi occupo principalmente dello sviluppo di{" "}
                <strong>applicazioni web complesse, CRM, ERP, piattaforme SaaS, CMS personalizzati e sistemi di automazione</strong>,
                seguendo l'intero ciclo di vita del software, dall'analisi dei requisiti alla progettazione, implementazione e
                distribuzione.
              </p>
              <p>
                Ho sviluppato soluzioni per diversi settori, tra cui{" "}
                <strong>
                  logistica e spedizioni, gestione doganale, turismo e booking online, automotive, immobiliare e servizi finanziari
                </strong>
                , con particolare attenzione alla scalabilità, all'usabilità e all'ottimizzazione dei processi aziendali.
              </p>
              <p>
                Negli ultimi anni ho ampliato le mie competenze nell'ambito dell'<strong>Intelligenza Artificiale</strong>,
                approfondendo l'integrazione di modelli linguistici (LLM), API AI, modelli open source e strumenti di automazione
                intelligente all'interno di applicazioni e processi software.
              </p>
              <p>
                Affianco alle competenze di sviluppo una solida esperienza nella gestione di infrastrutture web, server Linux,
                hosting, database e ambienti di produzione.
              </p>
              <p>
                Il mio approccio combina esperienza tecnica, autonomia progettuale e attenzione alle esigenze del cliente, con
                l'obiettivo di realizzare soluzioni concrete, efficienti e facilmente evolvibili.
              </p>
              <p className={classes.callout}>
                <strong>Portfolio, progetti e demo:</strong> <Link href="https://www.davidebalice.dev">www.davidebalice.dev</Link>
              </p>
            </div>
          </Row>
        </div>

        <Section>Esperienza lavorativa</Section>

        <Job
          date="2025 ad oggi"
          employer={
            <>
              DVA Express - Endesa Srl
              <br />
              <Link href="https://www.dvaexpress.com">www.dvaexpress.com</Link> -{" "}
              <Link href="https://www.easyparcel.it">www.easyparcel.it</Link>
            </>
          }
          sector="Logistica e sviluppo software"
          role={<h3>Full Stack Developer – Settore Logistica e Spedizioni</h3>}
        >
          <div className={classes.prose}>
            <p>
              Progettazione e sviluppo di piattaforme software per la logistica, le spedizioni internazionali, la gestione
              doganale e i processi CRM aziendali.
            </p>
            <p>
              Un'esperienza professionale particolarmente significativa, caratterizzata dalla partecipazione a progetti software
              complessi e ad alta operatività, utilizzati quotidianamente da migliaia di utenti.
            </p>
            <p>Tra le principali attività e responsabilità:</p>
            <ul>
              <li>
                <strong>EasyParcel:</strong> sviluppo ed evoluzione delle interfacce della piattaforma gestionale principale, con
                particolare attenzione all'esperienza utente, alle funzionalità operative e all'ottimizzazione dei flussi di lavoro.
              </li>
              <li>
                <strong>Software doganale:</strong> progettazione e sviluppo di funzionalità dedicate alla gestione delle
                operazioni doganali, con integrazione di motori di Intelligenza Artificiale per l'analisi, la generazione e
                l'elaborazione automatizzata dei dati.
              </li>
              <li>
                <strong>CRM aziendale:</strong> sviluppo di un sistema CRM per la gestione dei clienti, delle attività commerciali e
                dei processi operativi, con funzionalità personalizzate sulle esigenze aziendali come bot che automatizzano
                l'apertura pratiche assistenza sui portali dei vettori.
              </li>
            </ul>
            <p>
              Questa esperienza mi ha permesso di consolidare ulteriormente le competenze nella progettazione di soluzioni
              enterprise, nell'integrazione di sistemi complessi e nello sviluppo di applicazioni mission-critical,
              confrontandomi con esigenze concrete di affidabilità, prestazioni e scalabilità.
            </p>
          </div>
        </Job>

        <Job
          date="2025 ad oggi"
          employer={
            <>
              Aroundweb di Davide Balice - WebHub
              <br />
              <Link href="https://www.webhub.it">www.webhub.it</Link> - <Link href="https://www.aroundweb.it">www.aroundweb.it</Link>
            </>
          }
          sector="Sviluppo software"
          role={
            <h3>
              <em>WebHub – Founder &amp; Full Stack Developer</em>
            </h3>
          }
        >
          <div className={classes.prose}>
            <p>
              <strong>Sviluppo software, piattaforme web e soluzioni digitali</strong>
            </p>
            <p>
              Ideazione e sviluppo di <strong>WebHub</strong>, progetto dedicato alla realizzazione di piattaforme web,
              applicazioni gestionali e soluzioni digitali personalizzate per aziende e professionisti.
            </p>
            <p>
              Il progetto nasce dall'esperienza maturata in oltre 20 anni di sviluppo software, con l'obiettivo di offrire
              soluzioni tecnologiche moderne, flessibili e progettate sulle reali esigenze dei clienti.
            </p>
            <p>Tra le principali attività:</p>
            <ul>
              <li>
                <strong>Sviluppo di piattaforme verticali:</strong> progettazione di soluzioni dedicate a turismo, hotel,
                ristorazione, automotive, immobiliare, e-commerce, centri sportivi e altri settori con template e demo già pronte.
              </li>
              <li>
                <strong>Software e gestionali personalizzati:</strong> realizzazione di CRM, CMS proprietari, sistemi di
                prenotazione, dashboard e applicazioni web su misura.
              </li>
              <li>
                <strong>Intelligenza Artificiale:</strong> integrazione di modelli AI e LLM per automatizzare processi, elaborare
                contenuti e introdurre funzionalità intelligenti nelle piattaforme.
              </li>
              <li>
                <strong>3D, realtà virtuale ed esperienze immersive:</strong> sviluppo di configuratori 3D interattivi,
                visualizzatori di modelli tridimensionali, tour virtuali a 360°, ambienti navigabili ed esperienze immersive
                integrate in siti web e applicazioni, con particolare attenzione ai settori immobiliare, turistico ed e-commerce.
              </li>
              <li>
                <strong>Architettura e sviluppo Full Stack:</strong> gestione dell'intero ciclo di sviluppo, dalla progettazione
                delle interfacce e delle architetture software fino alla pubblicazione e manutenzione.
              </li>
              <li>
                <strong>Infrastruttura e DevOps:</strong> configurazione e gestione di server Linux, VPS, database, hosting e
                ambienti di produzione.
              </li>
            </ul>
            <p>
              WebHub rappresenta un percorso di <strong>innovazione, sperimentazione e sviluppo continuo</strong>, che unisce
              competenze tecniche, visione progettuale e attenzione all'esperienza utente, con l'obiettivo di trasformare esigenze
              aziendali in prodotti software concreti e scalabili.
            </p>
          </div>
        </Job>

        <Job
          date="2012 - 2025"
          employer={
            <>
              Gadaweb Snc di Balice e Scopelliti
              <br />
              <Link href="https://www.gadaweb.com">www.gadaweb.com</Link> - <Link href="https://www.sitishop.it">www.sitishop.it</Link>
            </>
          }
          sector={
            <>
              Società nome collettivo.
              <br />
              Azienda informatica specializzata nello sviluppo di piattaforme web, cms, piattaforme di prenotazioni online, app,
              software e gestionali.
            </>
          }
          role={<h3>Socio fondatore e full stack developer</h3>}
        >
          <div className={classes.prose}>
            <p>
              Sviluppo software e piattaforme web
              <br />
              Gadaweb nasce dalla necessità di portare i servizi sul territorio nazionale, realizzazione piattaforme Saas orientate
              al mondo del turismo.
              <br />
              Negli anni è stato sviluppato Sitiwebturismo.it, portale di servizi per Agenzie di viaggio, Tour operator e Strutture
              ricettive, Doyouall, piattaforma editor drag and drop per la realizzazione di siti web (attualmente non più online),
              e SitiShop.it, piattaforma responsive per la creazione di siti web con editor e moduli.
              <br />
              Tutti i servizi sono interconnessi tra loro e scalabili, e possono essere adattati alle esigenze dei clienti.
              Realizzare progetti così grandi ed impegnativi mi ha fatto crescere come professionista e sviluppatore, mi ha fatto
              sviluppare capacità di problem solving e capacità di rispettare scadenze.
            </p>
          </div>
        </Job>

        <Job
          date="2007 - 2012"
          employer={
            <>
              Aroundweb di Balice Davide
              <br />
              <Link href="https://www.aroundweb.it">www.aroundweb.it</Link>
            </>
          }
          sector="Ditta individuale. Web agency, sviluppo siti web e portali web"
          role={<h3>Web designer e sviluppatore</h3>}
        >
          <div className={classes.prose}>
            <p>
              Sviluppo e gestione
              <br />
              Nel 2006 ho aperto una web agency che si è contraddistinta per professionalità e creatività sul territorio.
              <br />
              Aroundweb è stata attiva per 6 anni, ho partecipato attivamente alle fasi di pianificazione e sviluppo dei progetti,
              utilizzando diverse tecnologie per garantire la funzionalità ottimale e l'esperienza utente di alto livello. Ho
              gestito le relazioni con i clienti, ascoltando le loro esigenze e traducendole in soluzioni tecnologiche.
              <br />
              Ho lavorato per clienti importanti come Rotary, Regione Calabria, Tv locali, Agenzie stampa, diversi politici tra cui
              il Presidente della Regione Calabria.
              <br />
              Poter gestire una web agency mi ha fatto crescere come professionista, e mi ha fatto sviluppare altre skill oltre a
              quelle tecniche, come la gestione dei rapporti con i clienti.
            </p>
          </div>
        </Job>

        <Job date="2005 – 2006" employer="Rhegion Travel Tour Operator" sector="Tour Operator ed Agenzia Viaggi" role={<h3>Web Master</h3>}>
          <div className={classes.prose}>
            <p>
              Sviluppo e gestione portale web prenotazioni turistiche strutture ricettive, catalogo Calabria. Sviluppo, gestione,
              seo, tecnologia Asp, Mdb, JavaScript. Questa prima esperienza lavorativa in ambito web mi ha permesso di apprendere
              nozioni oltre che tecniche, anche relative al mondo del turismo, nozioni che mi torneranno utili successivamente per
              lo sviluppo di piattaforme web di prenotazione online per tour operator.
            </p>
          </div>
        </Job>

        <Job date="2004" employer="Futura Snc" sector="Società informatica e negozio rivendita pc" role={<h3>Tecnico pc</h3>}>
          <div className={classes.prose}>
            <p>
              Assemblaggio e configurazione Pc. Configurazioni di rete.
              <br />
              Assistenza clienti.
            </p>
          </div>
        </Job>

        <Section>Istruzione e formazione</Section>

        <Edu
          date="2024"
          institute={
            <>
              Linux Professional Institute
              <br />
              <span className={classes.muted}>Web Development Essentials Exam 030-100</span>
            </>
          }
          subjects={
            <>
              Html, Css, Javascript, Node.js
              <br />
              Certificazione per sviluppatori web della Linux Professional Institute
            </>
          }
          qualification="Web Development Essentials"
        />
        <Edu
          date="2023"
          institute={
            <>
              Lacerba.io
              <br />
              <span className={classes.muted}>Fullstack Developer Java</span>
            </>
          }
          subjects={
            <>
              Html, Css, Javascript, Java
              <br />
              Corso online svolto principalmente per ripassare Java, soprattutto il framework Spring Boot, in modo da ampliare il
              mio bagaglio di conoscenze
            </>
          }
          qualification="Fullstack Developer Java"
        />
        <Edu
          date="2005"
          institute="Certificazione CCNA sistemista di rete Cisco"
          subjects={
            <>
              Progettazione, realizzazione e amministrazione reti informatiche complesse.
              <br />
              Configurare e gestire apparati Cisco come switch, router e bridge.
            </>
          }
          qualification="CCNA"
        />
        <Edu
          date="1994-1999"
          institute="Istituto Tecnico Commerciale “G. Ferraris”"
          subjects="Ragioneria"
          qualification="Diploma Ragioneria"
          level="70/100"
        />

        <Section>Capacità e competenze personali</Section>

        <div className={classes.block}>
          <Row label={<strong>Madrelingua</strong>}>
            <strong>Italiano</strong>
          </Row>
        </div>

        <div className={classes.block}>
          <Row label={<strong>Altre lingue</strong>}>
            <strong>Inglese</strong>
          </Row>
          <Row label="• Capacità di lettura">
            <Level text="buono" value={3} />
          </Row>
          <Row label="• Capacità di scrittura">
            <Level text="buono" value={3} />
          </Row>
          <Row label="• Capacità di espressione orale">
            <Level text="buono" value={3} />
          </Row>
        </div>

        <div className={classes.block}>
          <Row label={<strong>Capacità e competenze relazionali</strong>}>
            Di natura calmo e riflessivo, questa qualità mi consente di affrontare le sfide con serenità e determinazione,
            favorendo un ambiente di lavoro sereno e produttivo, instaurando rapporti di fiducia e collaborazione con clienti e
            colleghi.
            <br />
            Ottime competenze comunicative-relazionali e attitudine al contatto con la clientela acquisite grazie all'esperienza da
            sviluppatore e da titolare di web agency, capacità di capire le esigenze del cliente e di impostare il lavoro in base ad
            esse.
          </Row>
        </div>

        <div className={classes.block}>
          <Row label={<strong>Capacità e competenze organizzative</strong>}>
            Attitudine nella pianificazione, capacità di gestione di progetti complessi, predisposizione al perseguimento degli
            obiettivi stabiliti, capacità di lavorare sotto stress, rispetto delle scadenze dei progetti, ottima predisposizione a
            fronteggiare eventuali problemi che si presentano.
          </Row>
        </div>

        <div className={classes.block}>
          <Row label={<strong>Capacità e competenze tecniche</strong>}>
            <div className={classes.tech}>
              {TECH.map(([name, items]) => (
                <div key={name} className={classes.techRow}>
                  <span className={classes.techName}>{name}</span>
                  <span className={classes.chips}>
                    {items.map((it) => (
                      <span key={it}>{it}</span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </Row>
        </div>

        <div className={classes.block}>
          <Row label={<strong>Capacità e competenze artistiche</strong>}>
            Buone competenze di grafica 2D e grafica 3D. Software utilizzati Photoshop, Blender, Unity.
            <br />
            Le competenze artistiche acquisite e la forte creatività che mi contraddistingue, mi portano a disegnare spesso in
            autonomia i layout dei miei progetti, creando da zero anche icone o loghi.
            <br />
            Sono inoltre appassionato di grafica 3d, questo mi ha portato a sviluppare videogiochi con l'Engine Unity, e a
            sviluppare app 3D sul web tramite tecnologie native come three.js e React Three Fiber
          </Row>
        </div>

        <div className={classes.block}>
          <Row label={<strong>Competenze Trasversali</strong>}>
            <ul className={classes.checks}>
              <li>Eccellenti capacità di comunicazione e relazione con i clienti.</li>
              <li>Team working e gestione di progetti complessi.</li>
              <li>Problem solving e rispetto delle scadenze.</li>
              <li>
                Adattabilità e continua formazione su nuove tecnologie, utilizzando materiale online come corsi o video corsi
                (Udemy).
              </li>
            </ul>
          </Row>
        </div>

        <div className={classes.block}>
          <Row label={<strong>Patente o patenti</strong>}>
            <span className={classes.date}>B</span>
          </Row>
        </div>

        <div className={classes.bottomBar} />
      </article>
    </div>
  );
};

export default Cv;
